import z from 'zod'
import { Brand } from './enums'

export const MobileExperienceName = {
  AppLaunchCold: 'app.launch.cold',
  AppLaunchWarm: 'app.launch.warm',
  ScreenLoad: 'screen.load',
} as const

export const AuthExperienceName = {
  SignIn: 'auth.sign_in',
  SignOut: 'auth.sign_out',
} as const

export const DeliveryExperienceName = {
  DateUpdated: 'delivery.date_updated',
  AddressUpdated: 'delivery.address_updated',
  AddressServiceability: 'delivery.address_serviceability',
} as const

export const ExperienceName = {
  ...MobileExperienceName,
  ...AuthExperienceName,
  ...DeliveryExperienceName,
} as const

export const ExperienceNameSchema = z.nativeEnum(ExperienceName)
export type ExperienceName = z.infer<typeof ExperienceNameSchema>

export const ScreenNameSchema = z.enum(['sign_in', 'deliveries'])
export type ScreenName = z.infer<typeof ScreenNameSchema>

export const ExperienceReasonSchema = z.enum([
  'network_timeout',
  'validation_rejected',
  'navigated_away',
  'ttvc_timeout',
])
export type ExperienceReason = z.infer<typeof ExperienceReasonSchema>

export const ExperienceOutcomeSchema = z.enum(['success', 'failure', 'abort'])
export type ExperienceOutcome = z.infer<typeof ExperienceOutcomeSchema>

export const ExperienceTriggerSchema = z.enum(['tap', 'back', 'deep_link', 'tab_restore', 'launch'])
export type ExperienceTrigger = z.infer<typeof ExperienceTriggerSchema>

export const PlatformSchema = z.enum(['ios', 'web'])
export type Platform = z.infer<typeof PlatformSchema>

export const EXPERIENCE_CRITICALITY = {
  'app.launch.cold': true,
  'app.launch.warm': true,
  'screen.load': false,
  'auth.sign_in': true,
  'auth.sign_out': false,
  'delivery.date_updated': false,
  'delivery.address_updated': false,
  'delivery.address_serviceability': false,
} as const satisfies Record<ExperienceName, boolean>

/**
 * W3C Trace Context version 00: `00-<32 hex trace id>-<16 hex span id>-<2 hex flags>`.
 * Lowercase hex only. The two negative lookaheads reject the all-zero trace
 * id and the all-zero span id, both invalid per the W3C spec.
 */
const TRACEPARENT_PATTERN = /^00-(?!0{32})[0-9a-f]{32}-(?!0{16})[0-9a-f]{16}-[0-9a-f]{2}$/

const TraceparentSchema = z.string().regex(TRACEPARENT_PATTERN, {
  message: 'traceparent must be a W3C Trace Context version 00 header with lowercase hex ids and non-zero trace/span ids',
})

/** 16 lowercase hex chars, client-minted for the start event, all-zero rejected. */
const START_SPAN_ID_PATTERN = /^(?!0{16})[0-9a-f]{16}$/

const ExperienceRecordBase = z.object({
  name: ExperienceNameSchema,
  // Not every experience targets a screen. e.g. app.launch.cold does not. Mainly used for screen performance.
  target: ScreenNameSchema.optional(),
  traceparent: TraceparentSchema,
  // Wall-clock ISO 8601 timestamp used to place the span in time
  startedAt: z.string().datetime(),
  trigger: ExperienceTriggerSchema.optional(),
  attributes: z.record(z.string().max(64), z.string().max(200))
    .refine(attrs => Object.keys(attrs).length <= 8, {
      message: 'attributes may contain at most 8 keys',
    })
    .optional(),
})

const ExperienceStartRecordSchema = ExperienceRecordBase.extend({
  phase: z.literal('start'),
  spanId: z.string().regex(START_SPAN_ID_PATTERN, {
    message: 'spanId must be 16 lowercase hex characters and non-zero',
  }),
}).strict()

const ExperienceEndRecordSchema = ExperienceRecordBase.extend({
  phase: z.literal('end'),
  outcome: ExperienceOutcomeSchema,
  durationMs: z.number().int().nonnegative(),
  reason: ExperienceReasonSchema.optional(),
}).strict()

export const ExperienceRecordSchema = z.discriminatedUnion('phase', [
  ExperienceStartRecordSchema,
  ExperienceEndRecordSchema,
]).superRefine((record, ctx) => {
  if (record.phase !== 'start')
    return

  // Both records for one experience carry the SAME traceparent, so without a
  // second span id they collide on (trace_id, span_id)
  const traceparentSpanId = record.traceparent.split('-')[2]
  if (record.spanId === traceparentSpanId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['spanId'],
      message: 'spanId must differ from the traceparent span id',
    })
  }
})
export type ExperienceRecord = z.infer<typeof ExperienceRecordSchema>

export const ExperienceBatchSchema = z.object({
  brand: z.enum([Brand.LYKA, Brand.TYGA]),
  platform: PlatformSchema,
  appVersion: z.string().min(1).max(64),
  records: z.array(ExperienceRecordSchema).min(1).max(50),
})
export type ExperienceBatch = z.infer<typeof ExperienceBatchSchema>

export const EXPERIENCE_MAX_CLOCK_SKEW_FUTURE_MS = 5 * 60 * 1000 // +5 minutes
export const EXPERIENCE_MAX_CLOCK_SKEW_PAST_MS = 6 * 60 * 60 * 1000 // -6 hours
